import PropTypes from "prop-types"
import React, { Component, useContext } from "react"

const SCREENSIZES = ["sm", "md", "lg", "xl"]
const SCREENSIZES_REVERSED = SCREENSIZES.slice().reverse()

export const ScreensizeContext = React.createContext()

export class Provider extends Component {
  static displayName = "ScreensizeProvider"

  static getScreensize() {
    if (window.innerWidth <= 480) return "sm"
    if (window.innerWidth <= 768) return "md"
    if (window.innerWidth <= 992) return "lg"
    return "xl"
  }

  static propTypes = {
    screensize: PropTypes.oneOf(SCREENSIZES)
  }

  state = {
    screensize: Provider.getScreensize()
  }

  get screensize() {
    return this.props.screensize || this.state.screensize
  }

  UNSAFE_componentWillMount() {
    if (!this.props.screensize) {
      window.addEventListener("resize", this.handleResize)
    }
  }

  componentWillUnmount() {
    if (!this.props.screensize) {
      window.removeEventListener("resize", this.handleResize)
    }
  }

  handleResize = (e) => {
    const nextScreensize = Provider.getScreensize()
    if (nextScreensize !== this.state.screensize) {
      this.setState({ screensize: nextScreensize })
    }
  }

  render() {
    return (
      <ScreensizeContext.Provider value={this.screensize}>
        {this.props.children}
      </ScreensizeContext.Provider>
    )
  }
}

export const useScreensizeContext = () => {
  return useContext(ScreensizeContext)
}

export const withScreensize = (Component) => (props) =>
  (
    <ScreensizeContext.Consumer>
      {(screensize) => <Component screensize={screensize} {...props} />}
    </ScreensizeContext.Consumer>
  )

// Derive a screensize label using the same breakpoints as Provider.getScreensize,
// but for an arbitrary container width instead of window.innerWidth.
export function getScreensizeForWidth(width) {
  if (typeof width !== "number") return undefined
  if (width <= 480) return "sm"
  if (width <= 768) return "md"
  if (width <= 992) return "lg"
  return "xl"
}

// helper method
export function select(obj) {
  return function (props) {
    if (typeof obj === "function") obj = obj(props)
    if (!obj) return undefined
    // Resolve screensize: prefer explicit props.screensize, otherwise infer from container
    // dimensions if provided via props.containerWidth, props.container (ref), or props.element.
    let resolvedScreensize = props && props.screensize
    if (!resolvedScreensize && props) {
      let width
      if (typeof props.containerWidth === "number") {
        width = props.containerWidth
        // Assumes props.container is a ref to a DOM element
      } else if (
        props.container &&
        props.container.current &&
        props.container.current.clientWidth
      ) {
        width = props.container.current.clientWidth
        // Assumes props.element is a DOM element
      } else if (props.element && props.element.clientWidth) {
        width = props.element.clientWidth
      }
      const inferred = getScreensizeForWidth(width)
      if (inferred) resolvedScreensize = inferred
    }

    if (!resolvedScreensize) return undefined

    // Attempt to select the largest screensize setting available for the current screensize
    const screesizesCascade = SCREENSIZES_REVERSED.map((_, i) =>
      SCREENSIZES_REVERSED.slice(0, i + 1)
    )
    for (let i = 0; i < screesizesCascade.length; i++) {
      const screensize = SCREENSIZES_REVERSED[i]
      const cascade = screesizesCascade[i]
      if (cascade.includes(resolvedScreensize)) {
        if (obj.hasOwnProperty(screensize)) return obj[screensize]
      }
    }
  }
}

// const style = Screensize.select({ lg: 100, sm: 50 })
// style({ screensize: "xl" }) // => 100
// style({ screensize: "lg" }) // => 100
// style({ screensize: "md" }) // => 50
// style({ screensize: "sm" }) // => 50
// style({ screensize: null }) // => undefined

export default {
  Provider,
  Consumer: ScreensizeContext.Consumer,
  select,
  getScreensizeForWidth
}
